import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { MailCheck, ArrowLeft } from "lucide-react";

/**
 * ForgotPassword — Phase 16.3
 *
 * Enumeration-safe: the backend ALWAYS returns 200 with the same
 * message.  We show that message unconditionally on submit so no
 * signal about account existence ever leaks through the UI either.
 */
export default function ForgotPassword() {
  const nav = useNavigate();
  const [email, setEmail] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [err, setErr] = React.useState("");

  async function submit(e) {
    e.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/forgot", { email: email.trim() });
      setMessage(data.message || "Check your inbox.");
      setSent(true);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2?.response?.data?.detail) || e2?.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="forgot-password-page">
      <div className="hidden lg:flex flex-col justify-between p-10 bg-card border-r border-brand-rule">
        <div className="flex items-center gap-2">
          <div className="h-6 w-6 rounded-md bg-brand-violet flex items-center justify-center text-white font-bold text-[13px]">F</div>
          <div className="font-bold tracking-tight text-[15px] text-brand-ink">Fable Ops</div>
        </div>
        <div className="max-w-md">
          <div className="micro-label">Account recovery</div>
          <h1 className="mt-2 text-[28px] font-bold tracking-tight text-brand-ink leading-tight">
            Reset your password securely.
          </h1>
          <p className="mt-3 text-[13px] text-brand-muted">
            We'll email a one-time link that's valid for 45 minutes. The link
            can be used once and every existing session is signed out the
            moment your new password takes effect.
          </p>
          <div className="mt-6 space-y-1.5">
            {[
              ["Link TTL",    "45 minutes"],
              ["One-time",    "single use"],
              ["Session kill", "all devices"],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center gap-3 text-[12px]">
                <span className="micro-label w-24">{k}</span>
                <span className="mono-num text-brand-ink">{v}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="text-[11px] text-brand-faint">Phase 16.3 · auth-reset spine.</div>
      </div>

      <div className="flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <Link to="/login"
                className="inline-flex items-center gap-1 text-[11px] text-brand-muted hover:text-brand-ink"
                data-testid="forgot-back-to-login">
            <ArrowLeft size={12} /> Back to sign in
          </Link>
          <div className="mt-3 micro-label">Password reset</div>
          <h2 className="mt-1 text-[22px] font-bold tracking-tight text-brand-ink">
            Forgot your password?
          </h2>
          <p className="mt-1 text-[12px] text-brand-muted">
            Enter your account email — we'll send a reset link if the address is on file.
          </p>

          {!sent ? (
            <form onSubmit={submit} className="mt-6 space-y-3" data-testid="forgot-form">
              <div>
                <label className="micro-label">Email</label>
                <input type="email" required autoFocus className="field-input mt-1"
                       value={email} onChange={(e) => setEmail(e.target.value)}
                       autoComplete="email" data-testid="forgot-email" />
              </div>
              {err && (
                <div className="text-[12px] text-brand-coral" data-testid="forgot-error">{err}</div>
              )}
              <button type="submit" className="btn-violet w-full" disabled={busy || !email.trim()}
                      data-testid="forgot-submit">
                {busy ? "Sending…" : "Send reset link"}
              </button>
            </form>
          ) : (
            <div className="mt-6 rounded-md border border-emerald-300 bg-emerald-50 p-4"
                 data-testid="forgot-sent">
              <div className="flex items-start gap-2">
                <MailCheck className="text-emerald-700 mt-0.5" size={16} />
                <div>
                  <div className="text-[13px] font-semibold text-emerald-900">Check your inbox</div>
                  <div className="mt-1 text-[12px] text-emerald-900/80 leading-relaxed">
                    {message}
                  </div>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <button type="button" onClick={() => { setSent(false); setEmail(""); }}
                        className="btn-outline !h-8" data-testid="forgot-send-another">
                  Send another
                </button>
                <button type="button" onClick={() => nav("/login")}
                        className="btn-ghost !h-8" data-testid="forgot-go-login">
                  Back to sign in
                </button>
              </div>
            </div>
          )}
          <div className="mt-4 text-[11px] text-brand-faint leading-relaxed">
            To protect account privacy, Fable returns the same message whether or not
            an account exists for that email. Rate-limited to prevent enumeration.
          </div>
        </div>
      </div>
    </div>
  );
}
